import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DashboardLayout from "../components/DashboardLayout";
import { profileAPI } from "../services/api";

export default function BasicInfoPage() {
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.Auth);

    const [profileData, setProfileData] = useState({});
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        city: "",
        country: "India",
        gender: "",
        dateOfBirth: "",
        age: "",
        languages: [],
        bio: "",
    });

    const [loading, setLoading] = useState(false);

    // Expanded language options - Popular languages from around the world
    const languageOptions = [
        // Indian Languages
        "Hindi",
        "English",
        "Bengali",
        "Telugu",
        "Marathi",
        "Tamil",
        "Urdu",
        "Gujarati",
        "Kannada",
        "Malayalam",
        "Punjabi",
        "Odia",
        // International Languages
        "Spanish",
        "French",
        "German",
        "Italian",
        "Portuguese",
        "Russian",
        "Chinese (Mandarin)",
        "Japanese",
        "Korean",
        "Arabic",
        "Turkish",
        "Dutch",
        "Swedish",
        "Polish",
        "Thai",
        "Vietnamese",
        "Indonesian",
        "Malay",
        "Filipino (Tagalog)",
        "Greek"
    ];

    const countryOptions = [
        "India",
        "USA",
        "UK",
        "Canada",
        "Australia",
        "UAE",
        "Singapore",
        "Malaysia",
    ];

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                const storedUser = localStorage.getItem("user");
                if (!storedUser && !isAuthenticated) {
                    navigate("/login");
                    return;
                }

                setLoading(true);

                // Fetch profile data from API
                const response = await profileAPI.getProfile();

                if (response.data) {
                    const userData = response.data.data;

                    console.log('User Data:', userData);

                    // Update profile data state
                    setProfileData(userData);

                    // Update form data with API response
                    setFormData({
                        name: userData.name || "",
                        email: userData.email || "",
                        phone: userData.phone || "",
                        city: userData.get_trainer.city || "",
                        country: userData.get_trainer.country || "India",
                        gender: userData.get_trainer.gender || "",
                        dateOfBirth: userData.get_trainer.dob || "",
                        age: userData.get_trainer.age || "",
                        languages: Array.isArray(userData.get_trainer.languages) ? userData.get_trainer.languages :
                            (userData.get_trainer.languages ? userData.get_trainer.languages.split(',').map(l => l.trim()) : []),
                        bio: userData.get_trainer.about || "",
                    });

                    // Update localStorage with fresh data
                    localStorage.setItem('user', JSON.stringify(userData));
                }
            } catch (error) {
                console.error('Error fetching profile data:', error);
                toast.error('Failed to load profile data');

                // Fallback to localStorage if API fails
                const storedUser = localStorage.getItem("user");
                if (storedUser) {
                    const userData = JSON.parse(storedUser);
                    setProfileData(userData);
                    setFormData({
                        name: userData.name || "",
                        email: userData.email || "",
                        phone: userData.phone || "",
                        city: userData.get_trainer.city || "",
                        country: userData.get_trainer.country || "India",
                        gender: userData.get_trainer.gender || "",
                        dateOfBirth: userData.get_trainer.dob || "",
                        age: userData.get_trainer.age || "",
                        languages: userData.get_trainer.languages || [],
                        bio: userData.get_trainer.about || "",
                    });
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, [navigate, isAuthenticated]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Auto-calculate age from date of birth
        if (name === "dateOfBirth" && value) {
            const birthDate = new Date(value);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            setFormData((prev) => ({
                ...prev,
                age: age.toString(),
            }));
        }
    };

    const handleLanguageToggle = (language) => {
        setFormData((prev) => {
            const languages = prev.languages.includes(language)
                ? prev.languages.filter((lang) => lang !== language)
                : [...prev.languages, language];
            return { ...prev, languages };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log("=== BASIC INFO FORM SUBMIT STARTED ===");
        console.log("Form Data:", formData);

        // Validation
        if (!formData.name || !formData.gender || !formData.bio) {
            console.log("Validation failed: Missing required fields");
            toast.error("Please fill all required fields");
            return;
        }

        // Strip HTML tags for character count validation
        const bioTextOnly = formData.bio.replace(/<[^>]*>/g, '');
        console.log("Bio text length:", bioTextOnly.length);
        if (bioTextOnly.length < 50) {
            console.log("Validation failed: Bio too short");
            toast.error("Bio should be at least 50 characters long");
            return;
        }

        if (formData.languages.length === 0) {
            console.log("Validation failed: No languages selected");
            toast.error("Please select at least one language");
            return;
        }

        console.log("Validation passed! Proceeding to save...");
        setLoading(true);

        try {
            // Prepare data for API
            const dataToSend = {
                name: formData.name,
                phone: formData.phone,
                email: formData.email,
                city: formData.city,
                country: formData.country,
                gender: formData.gender,
                dob: formData.dateOfBirth,
                age: formData.age,
                languages: Array.isArray(formData.languages) ? formData.languages.join(', ') : formData.languages,
                about: formData.bio,
            };

            console.log("Data to send to API:", dataToSend);

            // Call API to update profile
            const response = await profileAPI.updateBasicInfo(dataToSend);
            console.log("API Response:", response);

            if (response.data.success || response.data.data) {
                const updatedData = response.data.data || response.data;

                // Update profile data state
                setProfileData(updatedData);

                // Update localStorage with fresh data from API
                localStorage.setItem("user", JSON.stringify(updatedData));

                console.log("Save completed successfully!");
                toast.success("Basic information updated successfully!");
            } else {
                throw new Error(response.data.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error("Save failed with error:", error);
            toast.error(error.response?.data?.message || "Failed to update information. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const getCompletionPercentage = () => {
        let completed = 0;
        const fields = [
            formData.name,
            formData.gender,
            formData.bio,
            formData.languages.length > 0,
            formData.country,
            formData.city,
        ];

        fields.forEach((field) => {
            if (field) completed++;
        });

        return Math.round((completed / fields.length) * 100);
    };

    const completionPercentage = getCompletionPercentage();

    return (
        <DashboardLayout activeTab="basic-info">
            <div className="basic-info-tab">
                <div className="tab-header">
                    <div>
                        <h3>
                            <i className="bi bi-person-fill me-2"></i>
                            Basic Information
                        </h3>
                        <p className="text-muted">Complete your basic profile information</p>
                    </div>
                    <div className="completion-badge">
                        <div className="mini-progress-circle">
                            <svg width="60" height="60">
                                <circle cx="30" cy="30" r="25" fill="none" stroke="#e9ecef" strokeWidth="5" />
                                <circle
                                    cx="30"
                                    cy="30"
                                    r="25"
                                    fill="none"
                                    stroke="#cc0000"
                                    strokeWidth="5"
                                    strokeDasharray={`${(completionPercentage / 100) * 157} 157`}
                                    transform="rotate(-90 30 30)"
                                    strokeLinecap="round"
                                />
                            </svg>
                            <div className="mini-percentage">{completionPercentage}%</div>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="profile-form" style={{ position: 'relative' }}>
                    {/* Loading Overlay - Only for Form Section */}
                    {loading && (
                        <div className="form-loading-overlay">
                            <div className="loading-content">
                                <div className="spinner-border text-danger" role="status" style={{ width: '3rem', height: '3rem' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading your profile data...</p>
                            </div>
                        </div>
                    )}

                    {/* Personal Details Section */}
                    <div className="form-section">
                        <h5 className="section-title">
                            <i className="bi bi-person-badge me-2"></i>
                            Personal Details
                        </h5>

                        <div className="row g-3">
                            <div className="col-md-6">
                                <label htmlFor="name" className="form-label">
                                    Full Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter your full name"
                                    required
                                />
                            </div>

                            <div className="col-md-6">
                                <label htmlFor="email" className="form-label">
                                    Email Address <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="email"
                                    className="form-control"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    disabled
                                    title="Email cannot be changed"
                                />
                                <small className="text-muted">Email cannot be changed</small>
                            </div>

                            <div className="col-md-6">
                                <label htmlFor="phone" className="form-label">
                                    Phone Number <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="tel"
                                    className="form-control"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="Enter phone number"
                                    required
                                />
                            </div>

                            <div className="col-md-6">
                                <label htmlFor="gender" className="form-label">
                                    Gender <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-select"
                                    id="gender"
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            <div className="col-md-6">
                                <label htmlFor="dateOfBirth" className="form-label">
                                    Date of Birth
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="dateOfBirth"
                                    name="dateOfBirth"
                                    value={formData.dateOfBirth}
                                    onChange={handleInputChange}
                                    max={new Date().toISOString().split("T")[0]}
                                />
                            </div>

                            <div className="col-md-6">
                                <label htmlFor="age" className="form-label">
                                    Age
                                </label>
                                <input
                                    type="number"
                                    className="form-control"
                                    id="age"
                                    name="age"
                                    value={formData.age}
                                    readOnly
                                    placeholder="Auto-calculated from DOB"
                                />
                                <small className="text-muted">Auto-calculated from date of birth</small>
                            </div>
                        </div>
                    </div>

                    {/* Location Section */}
                    <div className="form-section">
                        <h5 className="section-title">
                            <i className="bi bi-geo-alt-fill me-2"></i>
                            Location
                        </h5>

                        <div className="row g-3">
                            <div className="col-md-6">
                                <label htmlFor="country" className="form-label">
                                    Country <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-select"
                                    id="country"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    required
                                >
                                    {countryOptions.map((country) => (
                                        <option key={country} value={country}>
                                            {country}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-6">
                                <label htmlFor="city" className="form-label">
                                    City <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="city"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    placeholder="Enter your city"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Languages Section */}
                    <div className="form-section">
                        <h5 className="section-title">
                            <i className="bi bi-translate me-2"></i>
                            Languages Spoken <span className="text-danger">*</span>
                        </h5>
                        <p className="text-muted mb-3">Select all languages you can communicate in</p>

                        <div className="language-grid">
                            {languageOptions.map((language) => (
                                <div
                                    key={language}
                                    className={`language-chip ${formData.languages.includes(language) ? "selected" : ""
                                        }`}
                                    onClick={() => handleLanguageToggle(language)}
                                >
                                    <i
                                        className={`bi ${formData.languages.includes(language)
                                            ? "bi-check-circle-fill"
                                            : "bi-circle"
                                            } me-2`}
                                    ></i>
                                    {language}
                                </div>
                            ))}
                        </div>
                        {formData.languages.length > 0 && (
                            <div className="selected-languages mt-3">
                                <strong>Selected:</strong> {formData.languages.join(", ")}
                            </div>
                        )}
                    </div>

                    {/* Bio Section */}
                    <div className="form-section">
                        <h5 className="section-title">
                            <i className="bi bi-file-text-fill me-2"></i>
                            About Me / Bio <span className="text-danger">*</span>
                        </h5>
                        <p className="text-muted mb-3">
                            Write a compelling bio that highlights your expertise and passion for fitness
                            (minimum 50 characters). Use the editor to format your text.
                        </p>

                        <ReactQuill
                            theme="snow"
                            value={formData.bio}
                            onChange={(content) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    bio: content,
                                }));
                            }}
                            modules={{
                                toolbar: [
                                    [{ 'header': [1, 2, 3, false] }],
                                    ['bold', 'italic', 'underline'],
                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                    ['link'],
                                    ['clean']
                                ],
                            }}
                            formats={[
                                'header',
                                'bold', 'italic', 'underline',
                                'list', 'bullet',
                                'link'
                            ]}
                            placeholder="Example: I'm a certified fitness trainer with 5+ years of experience helping clients achieve their fitness goals. My specialization includes weight loss, muscle building, and functional training..."
                            className="bio-editor"
                        />
                        <div className="character-count mt-2">
                            <span className={formData.bio.replace(/<[^>]*>/g, '').length < 50 ? "text-danger" : "text-success"}>
                                {formData.bio.replace(/<[^>]*>/g, '').length} characters
                            </span>
                            {formData.bio.replace(/<[^>]*>/g, '').length < 50 && (
                                <span className="text-muted ms-2">
                                    (Minimum 50 characters required)
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="form-actions">
                        <button
                            type="submit"
                            className="btn btn-danger btn-lg"
                            disabled={loading}
                            onClick={() => console.log("Save button clicked!")}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-circle me-2"></i>
                                    Save Basic Information
                                </>
                            )}
                        </button>
                        {completionPercentage < 100 && (
                            <p className="text-warning mt-2 mb-0">
                                <i className="bi bi-info-circle me-1"></i>
                                Complete all fields for 100% profile completion
                            </p>
                        )}
                    </div>
                </form>
            </div>
        </DashboardLayout>
    );
}
